import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip,
    CircularProgress, Snackbar, Alert, IconButton, Tooltip, Autocomplete
} from '@mui/material'
import { Add, Edit, DeleteOutline } from '@mui/icons-material'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api } from '../../helpers'
import { countries } from '../../helpers/countries'
import _ from 'lodash'

interface Installer {
    _id: string
    name: string
    phone: string
    country: string
    areas: string[]
}

interface UserOption {
    _id: string
    Username: string
    Phone: string
    country?: string
}

interface FormState {
    name: string
    country: string
}

interface StateProps {
    installers: Installer[]
    loading: boolean
    showModal: boolean
    editingId: string | null
    deletingId: string | null
    saving: boolean
    form: FormState
    error: string | null
    userOptions: UserOption[]
    usersLoading: boolean
    selectedUser: UserOption | null
}

const defaultForm: FormState = {
    name: '',
    country: 'Kenya'
}

const headers = ['Name', 'Phone', 'Country', 'Coverage Areas', 'Actions']

const Installers: FC = () => {
    const [state, setState] = useState<StateProps>({
        installers: [],
        loading: true,
        showModal: false,
        editingId: null,
        deletingId: null,
        saving: false,
        form: defaultForm,
        error: null,
        userOptions: [],
        usersLoading: false,
        selectedUser: null
    })
    const { installers, loading, showModal, editingId, deletingId, saving, form, error, userOptions, usersLoading, selectedUser } = state

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    const getInstallers = () => {
        setState(s => ({ ...s, loading: true }))
        Api({ controller: 'Installers', action: 'getInstallers' })
            .then((resp: any) => setState(s => ({ ...s, installers: resp || [], loading: false })))
            .catch(() => setState(s => ({ ...s, loading: false })))
    }

    const searchUsers = (value: string) => {
        if (!value) return setState(s => ({ ...s, userOptions: [], usersLoading: false }))
        setState(s => ({ ...s, usersLoading: true }))
        Api({ controller: 'Users', action: 'searchUser', value })
            .then((resp: any) => setState(s => ({ ...s, userOptions: resp || [], usersLoading: false })))
            .catch(() => setState(s => ({ ...s, usersLoading: false })))
    }
    const debouncedSearchUsers = useCallback(_.debounce(searchUsers, 500), [])

    const openAdd = () => setState(s => ({
        ...s, showModal: true, editingId: null, form: defaultForm, userOptions: [], selectedUser: null
    }))

    const openEdit = (installer: Installer) => {
        setState(s => ({
            ...s,
            showModal: true,
            editingId: installer._id,
            selectedUser: null,
            form: { name: installer.name, country: installer.country }
        }))
    }

    const deleteInstaller = (installerId: string) => {
        setState(s => ({ ...s, deletingId: installerId }))
        Api({ controller: 'Installers', action: 'deleteInstaller', installerId })
            .then(() => {
                setState(s => ({ ...s, deletingId: null }))
                getInstallers()
            })
            .catch((err: any) => setState(s => ({ ...s, deletingId: null, error: err?.message || 'Failed to remove installer.' })))
    }

    const saveInstaller = () => {
        setState(s => ({ ...s, saving: true }))
        const payload: any = editingId
            ? { installerId: editingId, name: form.name, country: form.country }
            : { name: form.name, country: form.country, phone: selectedUser?.Phone }
        Api({ controller: 'Installers', action: editingId ? 'updateInstaller' : 'addInstaller', ...payload })
            .then((resp: any) => {
                if (resp?.error)
                    setState(s => ({ ...s, saving: false, error: resp.error.message }))
                else {
                    setState(s => ({ ...s, saving: false, showModal: false, editingId: null, form: defaultForm, selectedUser: null, userOptions: [] }))
                    getInstallers()
                }
            })
            .catch((err: any) => setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong.' })))
    }

    const canSave = !saving && !!form.name.trim() && !!form.country && (editingId ? true : !!selectedUser?.Phone)

    useEffect(() => { getInstallers() }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Installers</Typography>
                <Typography sx={{ color: '#6b7280', fontSize: 14 }}>
                    People authorized to install gadgets in a given country. They sign in on the app normally -
                    their Profile picks this up by phone number and shows them their pending installations.
                </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: '5px', mb: 1 }}>
                <Button variant='contained' startIcon={<Add />} onClick={openAdd} sx={{ borderRadius: 2 }}>
                    Add Installer
                </Button>
            </Box>

            <Show>
                <Show.When isTrue={loading}>
                    <EmptyView loading={loading} oopTxt='No Installers' text='No installers found.' />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 1, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {installers.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={headers.length} align='center' sx={{ color: '#9ca3af', py: 4 }}>
                                            No installers yet. Add your first one.
                                        </TableCell>
                                    </TableRow>
                                ) : installers.map(installer => (
                                    <TableRow key={installer._id} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{installer.name}</TableCell>
                                        <TableCell>{installer.phone}</TableCell>
                                        <TableCell>{installer.country}</TableCell>
                                        <TableCell sx={{ maxWidth: 260 }}>
                                            {installer.areas?.length ? (
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                    {installer.areas.map(area => (
                                                        <Chip key={area} label={area} size='small' />
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: '0.8rem', color: '#9ca3af' }}>Not set yet</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title='Edit'>
                                                <IconButton size='small' onClick={() => openEdit(installer)} sx={{ color: 'primary.main' }}>
                                                    <Edit fontSize='small' />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title='Delete'>
                                                <IconButton
                                                    size='small'
                                                    onClick={() => deleteInstaller(installer._id)}
                                                    disabled={deletingId === installer._id}
                                                    sx={{ color: '#ef4444', ml: 0.5 }}
                                                >
                                                    {deletingId === installer._id
                                                        ? <CircularProgress size={14} color='inherit' />
                                                        : <DeleteOutline fontSize='small' />}
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            <Dialog open={showModal} onClose={() => !saving && setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>{editingId ? 'Edit Installer' : 'Add Installer'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    {!editingId && (
                        <Autocomplete
                            options={userOptions}
                            getOptionLabel={(o) => o.Username}
                            loading={usersLoading}
                            value={selectedUser}
                            onChange={(_, val) => setState(s => ({
                                ...s,
                                selectedUser: val,
                                form: {
                                    name: val?.Username || '',
                                    country: val?.country || s.form.country
                                }
                            }))}
                            onInputChange={(_, val) => debouncedSearchUsers(val)}
                            isOptionEqualToValue={(o, v) => o._id === v._id}
                            noOptionsText='Type a username or phone number'
                            renderOption={(props, o) => (
                                <li {...props} key={o._id}>
                                    <Box>
                                        <Typography sx={{ fontWeight: 'bold', fontSize: 14 }}>{o.Username}</Typography>
                                        <Typography sx={{ color: '#727A8A', fontSize: 12 }}>{o.Phone}</Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label='Search User'
                                    placeholder='Type a username or phone number...'
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        )
                                    }}
                                />
                            )}
                        />
                    )}
                    {selectedUser && (
                        <Typography sx={{ fontSize: 13, color: '#6b7280', mt: -1 }}>
                            Selected: {selectedUser.Username} · {selectedUser.Phone}
                        </Typography>
                    )}
                    <TextField
                        label='Display Name'
                        value={form.name}
                        onChange={e => setForm({ name: e.target.value })}
                        fullWidth
                        size='small'
                        disabled={!editingId && !selectedUser}
                    />
                    <TextField
                        select
                        label='Country'
                        value={form.country}
                        onChange={e => setForm({ country: e.target.value })}
                        fullWidth
                        size='small'
                    >
                        {countries.map(c => (
                            <MenuItem key={c.name} value={c.name}>{c.flag} {c.name}</MenuItem>
                        ))}
                    </TextField>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveInstaller}
                        disabled={!canSave}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        {editingId ? 'Update' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Installers
